import Papa, { ParseResult } from 'papaparse';
import { ChangeEvent, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import useApiCall from '../../../../hooks/useApiCall';
import useGetGeneralAppData from '../../../../hooks/useGetGeneralAppData';
import WorkplacesService from '../../../../services/WorkplacesService';
import hasDesiredKeys from '../../../../utils/hasDesiredKeys';
import hasDesiredKeysAndValues from '../../../../utils/hasDesiredKeysAndValues';
import hasDuplicates from '../../../../utils/hasDuplicatedValueAtArray';
import isCepValid from '../../../../utils/isCepValid';
import removeDuplicates from '../../../../utils/removeDuplicates';
import renderArray from '../../../../utils/renderArrayWithComa';
import toUTF8 from '../../../../utils/toUTF8';
import { CreateWorkplaceType } from '../../types';

interface UseUploadFileInterface {
  loadWorkplaces: () => void;
}

interface CsvDataInterface {
  Denominação: string,
  'Código no cliente': string,
  Status: 'Ativo' | 'Inativo',
  CEP: string,
  Logradouro: string,
  Número: number,
  Complemento: string,
  Bairro: string,
  Cidade: string,
  UF: string,
  'Sem Integra': number,
  Latitude: string,
  Longitude: string,
  'Utiliza Fretamento?': 'Sim' | 'Não',
  'Código OP Fretamento': number,
  'Usa VT + Fretamento?': 'Sim' | 'Não',
  'Valor Teto': number,
}

export default function useUploadFile({
  loadWorkplaces,
}: UseUploadFileInterface) {
  const [isSubmittingFile, setIsSubmittingFile] = useState(false);
  const [csvFileName, setCsvFileName] = useState('');
  const [csvParsed, setCsvParsed] = useState<CreateWorkplaceType[]>([]);
  const [importModalShow, setImportModalShow] = useState(false);

  const { apiCall } = useApiCall();

  const { companyCode, userCode, userProfile } = useGetGeneralAppData();

  async function handleFileUpload(e: ChangeEvent<HTMLInputElement>) {
    const { files } = e.target;
    if (files) {
      setCsvFileName(files[0].name);

      const utf8File = await toUTF8(files[0]);
      console.log(utf8File);

      Papa.parse(utf8File, {
        header: true,
        complete: (results: ParseResult<CsvDataInterface>) => {
          console.log(results.data);
          const desiredHeaders = ['Código no cliente', 'CEP', 'Logradouro', 'Número', 'Complemento', 'Bairro', 'Cidade', 'UF', 'Denominação', 'Sem Integra', 'Valor Teto'];
          const filteredResult = results.data?.filter((index) => !!index.Denominação);

          if (!hasDesiredKeys(filteredResult, desiredHeaders) || filteredResult.length === 0) {
            toast.error('O arquivo não contém os cabeçalhos desejados - Certifique-se de sempre salvá-lo em CSV UTF-8');
            setCsvFileName('');
            return;
          }

          const requiredFields = ['Código no cliente', 'CEP', 'Logradouro', 'Bairro', 'Cidade', 'UF', 'Denominação', 'Sem Integra', 'Valor Teto'];
          if (!hasDesiredKeysAndValues(filteredResult, requiredFields)) {
            toast.error('O arquivo não contém todos os campos obrigatórios preenchidos');
            setCsvFileName('');
            return;
          }

          const invalidNames = filteredResult.filter((index) => !index.Denominação);
          if (invalidNames.length > 0) {
            const mappedNamesAndLines = invalidNames.map((index) => {
              const invalidName = index.Denominação;
              const line = filteredResult.indexOf(index) + 2;

              return { invalidName, line };
            });

            toast.error(`Denominações inválidas: ${renderArray(mappedNamesAndLines.map((x) => `${x.invalidName} - Linha ${x.line}`))}`, { autoClose: false });
          }

          const invalidCodes = filteredResult.filter((index) => !index['Código no cliente']);
          if (invalidCodes.length > 0) {
            const mappedNamesAndLines = invalidCodes.map((index) => {
              const invalidCode = index['Código no cliente'];
              const line = filteredResult.indexOf(index) + 2;

              return { invalidCode, line };
            });

            toast.error(`Códigos inválidas: ${renderArray(mappedNamesAndLines.map((x) => `${x.invalidCode} - Linha ${x.line}`))}`, { autoClose: false });
          }

          const invalidCeps = filteredResult.filter((index) => !index['CEP'] || !isCepValid(index['CEP']));
          if (invalidCeps.length > 0) {
            const mappedNamesAndLines = invalidCeps.map((index) => {
              const invalidCep = index['CEP'];
              const line = filteredResult.indexOf(index) + 2;

              return { invalidCep, line };
            });

            toast.error(`CEP's inválidas: ${renderArray(mappedNamesAndLines.map((x) => `${x.invalidCep} - Linha ${x.line}`))}`, { autoClose: false });
          }

          const invalidNumbers = filteredResult.filter((index) => isNaN(Number(index['Número'])));
          if (invalidNumbers.length > 0) {
            const mappedNamesAndLines = invalidNumbers.map((index) => {
              const invalidNumber = index['Número'];
              const line = filteredResult.indexOf(index) + 2;

              return { invalidNumber, line };
            });

            toast.error(`Números inválidas: ${renderArray(mappedNamesAndLines.map((x) => `${x.invalidNumber} - Linha ${x.line}`))}`, { autoClose: false });
          }

          const invalidSemIntegra = filteredResult.filter((index) => index['Sem Integra'] != 0 && index['Sem Integra'] != 1);
          if (invalidSemIntegra.length > 0) {
            const mappedSemIntegraAndLines = invalidSemIntegra.map((index) => {
              const invalidSemIntegra = index['Sem Integra'];
              const line = filteredResult.indexOf(index) + 2;

              return { invalidSemIntegra, line };
            });

            toast.error(`Sem Integra inválidos: ${renderArray(mappedSemIntegraAndLines.map((x) => `${x.invalidSemIntegra} - Linha ${x.line}`))}`, { autoClose: false });
          }

          const invalidCeilingValues = filteredResult.filter((index) => isNaN(Number(index['Valor Teto'])));
          if (invalidCeilingValues.length > 0) {
            const mappedCeilingValuesAndLines = invalidCeilingValues.map((index) => {
              const invalidCeilingValue = index['Valor Teto'];
              const line = filteredResult.indexOf(index) + 2;

              return { invalidCeilingValue, line };
            });

            toast.error(`Valor Teto inválidos: ${renderArray(mappedCeilingValuesAndLines.map((x) => `${x.invalidCeilingValue} - Linha ${x.line}`))}`, { autoClose: false });
          }

          const validUfs = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO',];
          const invalidUfs = filteredResult.filter((index) => !index.UF || !(validUfs.includes(index.UF)));
          if (invalidUfs.length > 0) {
            const mappedUfsAndLines = invalidUfs.map((index) => {
              const invalidUf = index.UF;
              const line = filteredResult.indexOf(index) + 2;

              return { invalidUf, line };
            });

            toast.error(`UF's inválidas: ${renderArray(mappedUfsAndLines.map((x) => `${x.invalidUf} - Linha ${x.line}`))}`, { autoClose: false });
          }

          // const validValues = ['Sim', 'Não'];
          // const invalidUsesAffreightments = filteredResult.filter((index) => !index['Utiliza Fretamento?'] || !(validValues.includes(index['Utiliza Fretamento?'])));
          // if (invalidUsesAffreightments.length > 0) {
          //   const mappedUsesAffreightmentAndLines = invalidUsesAffreightments.map((index) => {
          //     const invalidUsesAffreightment = index['Utiliza Fretamento?'];
          //     const line = filteredResult.indexOf(index) + 2;

          //     return { invalidUsesAffreightment, line };
          //   });

          //   toast.error(`Utiliza Fretamento inválidos: ${renderArray(mappedUsesAffreightmentAndLines.map((x) => `${x.invalidUsesAffreightment} - Linha ${x.line}`))}`, { autoClose: false });
          // }

          // const invalidUsesAffreightmentAndVts = filteredResult.filter((index) => !index['Usa VT + Fretamento?'] || !(validValues.includes(index['Usa VT + Fretamento?'])));
          // if (invalidUsesAffreightmentAndVts.length > 0) {
          //   const mappedUsesAffreightmentAndLines = invalidUsesAffreightmentAndVts.map((index) => {
          //     const invalidUsesAffreightmentAndVt = index['Usa VT + Fretamento?'];
          //     const line = filteredResult.indexOf(index) + 2;

          //     return { invalidUsesAffreightmentAndVt, line };
          //   });

          //   toast.error(`Usa VT + Fretamento inválidos: ${renderArray(mappedUsesAffreightmentAndLines.map((x) => `${x.invalidUsesAffreightmentAndVt} - Linha ${x.line}`))}`, { autoClose: false });
          // }

          const isSomethingInvalid = invalidNames.length > 0 || invalidCodes.length > 0 || invalidCeps.length > 0 || invalidNumbers.length > 0 || invalidUfs.length > 0 || invalidSemIntegra.length > 0 || invalidCeilingValues.length > 0;

          if (isSomethingInvalid) {
            setCsvFileName('');
            return;
          }

          setCsvParsed(filteredResult.map((index) => ({
            tipoEnd: 4,
            codEnd: 0,
            endAtivo: index['Status'].toLowerCase() === 'Ativo'.toLowerCase(),
            codEndNoclienteAlfaN: index['Código no cliente'].slice(0, 25),
            cep: index.CEP,
            logradouro: index.Logradouro.slice(0, 100),
            numero: Number(index.Número) || 0,
            complemento: index.Complemento.slice(0, 20) || '',
            bairro: index.Bairro.slice(0, 25),
            cidade: index.Cidade.slice(0, 30),
            UF: index.UF.slice(0, 2),
            denominacaoLT: index.Denominação.slice(0, 40),
            lat: 0,
            lng: 0,
            temFretamento: false,
            codFreta: 0,
            opcaoVTFreta: true,
            valorTeto: Number(index['Valor Teto']) || 0,
            distancia: 0,
            dataCadEnd: new Date().toISOString(),
            dataAtualEnd: new Date().toISOString(),
            semIntegrarRJ: index['Sem Integra'] == 1,
            semTaxaSPtrans: false,
            temLimitadorKM: false,
            KM: 0,
          })));
        },
      });
    }
  }

  const handleSubmitFile = useCallback(async () => {
    const csvParsedWithoutNullValues = csvParsed.filter((list) => !!list.denominacaoLT);
    const internalClientCodes = csvParsed.map((index) => index.codEndNoclienteAlfaN);

    if (hasDuplicates(internalClientCodes)) {
      toast.error('Foram encontrados 2 códigos no cliente repetidos no arquivo');
      setIsSubmittingFile(false);
      return;
    }

    await apiCall({
      apiToCall: WorkplacesService.createManyWorkplaces,
      reqBody: JSON.stringify({
        codEmpresa: companyCode,
        codUsu: userCode,
        perfilUsu: userProfile,
        tipoAcao: 10,
        dadosEndereco: csvParsedWithoutNullValues,
      }),
      onStartLoad: () => {
        setImportModalShow(false);
        setIsSubmittingFile(true);
      },
      onEndLoad: () => setIsSubmittingFile(false),
      actionAfterResponse: (apiResponse) => {
        if (!apiResponse.codigo) {
          setIsSubmittingFile(false);
          let codesWithErrorsString = '';
          const hasErrorsFromWorkplacesCode = apiResponse.listaCodDuplicado && apiResponse.listaCodDuplicado.length > 0;

          if (hasErrorsFromWorkplacesCode) {
            codesWithErrorsString = renderArray(removeDuplicates(apiResponse.listaCodDuplicado.map((x: { codDuplicado: string }) => x.codDuplicado))) || '';
          }

          toast.error(`Não foi possível importar o arquivo (${apiResponse.msg}${hasErrorsFromWorkplacesCode ? ` -> Códigos de local de trabalho duplicados: ${codesWithErrorsString}` : ''})`);
          return;
        }
        loadWorkplaces();
        setIsSubmittingFile(false);
        if (apiResponse.duplicidade) {
          toast.warn(`Os locais foram parcialmente importados (${apiResponse.msgDuplicidade})`);
        } else {
          toast.success('Os locais foram importados com sucesso');
        }
      },
      catchMessage: 'Não foi possível importar os locais',
      catchAction: () => setIsSubmittingFile(false),
    });
  }, [csvParsed, apiCall, companyCode, userCode, userProfile, loadWorkplaces]);

  function handleImportShow() {
    setImportModalShow(true);
  }

  function handleImportModalClose() {
    setImportModalShow(false);
  }

  return {
    isSubmittingFile,
    csvFileName,
    importModalShow,
    handleFileUpload,
    handleSubmitFile,
    handleImportShow,
    handleImportModalClose,
    setImportModalShow,
  };
}
